/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../../styles/ui/Button';
import { FormGrid } from '../../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../../styles/forms/Field';
import {
   createDependent,
   updateDependent,
} from '../../../../../services/hr/recruitment/dependentsApi';
import { DEPENDENT_RELATIONSHIP_OPTIONS } from '../../constants';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Criar/editar dependente do candidato (padrão OriginFormModal: prop
// `dependent` null = criar). Mutações REGENERAM o checklist no backend — o
// onSaved do pai recarrega dependentes E documentos. CPF vai só com dígitos
// (11 quando preenchido); opcionais vazios são omitidos (Zod rejeita null).
// ---------------------------------------------------------------------------

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

const EMPTY_FORM = {
   full_name: '',
   birth_date: '',
   cpf: '',
   relationship: '',
};

function onlyDigits(value) {
   return String(value ?? '').replace(/\D/g, '');
}

export default function DependentFormModal({
   isOpen,
   onClose,
   candidateId,
   dependent,
   onSaved,
}) {
   const isEdit = Boolean(dependent);
   const [form, setForm] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      if (isOpen) {
         setForm({
            full_name: dependent?.full_name ?? '',
            birth_date: dependent?.birth_date?.substring(0, 10) ?? '',
            // CPF chega formatado do backend — o input edita só dígitos.
            cpf: onlyDigits(dependent?.cpf),
            relationship: dependent?.relationship ?? '',
         });
         setFieldErrors({});
         setIsSubmitting(false);
      }
   }, [isOpen, dependent]);

   const handleChange = (event) => {
      const { name, value } = event.target;
      setForm((prev) => ({
         ...prev,
         [name]: name === 'cpf' ? onlyDigits(value).slice(0, 11) : value,
      }));
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
   };

   const handleConfirm = async () => {
      if (isSubmitting) return;

      if (!form.full_name.trim()) {
         setFieldErrors({ full_name: 'Informe o nome do dependente.' });
         return;
      }
      if (form.cpf && form.cpf.length !== 11) {
         setFieldErrors({ cpf: 'CPF deve ter 11 dígitos.' });
         return;
      }

      const payload = { full_name: form.full_name.trim() };
      if (form.birth_date) payload.birth_date = form.birth_date;
      if (form.cpf) payload.cpf = form.cpf;
      if (form.relationship) payload.relationship = form.relationship;

      setIsSubmitting(true);
      setFieldErrors({});
      try {
         if (isEdit) {
            const { message } = await updateDependent(
               candidateId,
               dependent.id,
               payload,
            );
            toast.success(message);
         } else {
            const { message } = await createDependent(candidateId, payload);
            toast.success(message);
         }
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors: apiFieldErrors } = readApiError(err);
         if (Object.keys(apiFieldErrors).length) {
            setFieldErrors(apiFieldErrors);
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao salvar o dependente.'),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={isEdit ? 'Editar dependente' : 'Novo dependente'}
         maxWidth="560px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
               >
                  {isSubmitting ? 'Salvando…' : 'Salvar'}
               </PrimaryButton>
            </>
         }
      >
         <FormGrid>
            <Field className="col-12">
               <label htmlFor="dependent-full-name">
                  Nome completo
                  <RequiredMark>*</RequiredMark>
               </label>
               <input
                  id="dependent-full-name"
                  type="text"
                  name="full_name"
                  maxLength={120}
                  value={form.full_name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  aria-invalid={!!fieldErrors.full_name || undefined}
               />
               {fieldErrors.full_name && (
                  <FieldError>{fieldErrors.full_name}</FieldError>
               )}
            </Field>

            <Field className="col-4">
               <label htmlFor="dependent-birth-date">Nascimento</label>
               <input
                  id="dependent-birth-date"
                  type="date"
                  name="birth_date"
                  value={form.birth_date}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  aria-invalid={!!fieldErrors.birth_date || undefined}
               />
               {fieldErrors.birth_date && (
                  <FieldError>{fieldErrors.birth_date}</FieldError>
               )}
            </Field>

            <Field className="col-4">
               <label htmlFor="dependent-cpf">CPF</label>
               <input
                  id="dependent-cpf"
                  type="text"
                  name="cpf"
                  inputMode="numeric"
                  placeholder="Somente números"
                  value={form.cpf}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  aria-invalid={!!fieldErrors.cpf || undefined}
               />
               {fieldErrors.cpf && <FieldError>{fieldErrors.cpf}</FieldError>}
            </Field>

            <Field className="col-4">
               <label htmlFor="dependent-relationship">Parentesco</label>
               <select
                  id="dependent-relationship"
                  name="relationship"
                  value={form.relationship}
                  onChange={handleChange}
                  disabled={isSubmitting}
               >
                  <option value="">Selecione…</option>
                  {DEPENDENT_RELATIONSHIP_OPTIONS.map((option) => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </select>
            </Field>
         </FormGrid>

         <p
            style={{
               margin: '12px 0 0',
               fontSize: '0.78rem',
               color: '#6b7280',
            }}
         >
            A idade do dependente define os documentos exigidos no checklist
            (certidão de nascimento/RG, CPF, cartão de vacinação, certidão
            escolar).
         </p>
      </Modal>
   );
}

DependentFormModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   dependent: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      full_name: PropTypes.string,
      birth_date: PropTypes.string,
      cpf: PropTypes.string,
      relationship: PropTypes.string,
   }),
   onSaved: PropTypes.func,
};
